import { colorKeyFromMap, getBadgeColorClasses, getTextColorClass, getChartFill, getBgColorClass, TailwindColorKey } from "./color-utils";

/** Rate Band Colors */
type RateBand = 'low' | 'medium' | 'high';

const RATE_BAND_COLORS: Record<RateBand, TailwindColorKey> = {
    low: 'red',
    medium: 'yellow',
    high: 'green',
};

/** Active Rate Band Colors */
function activeRateBand(activeRate: number): RateBand {
    if (activeRate < 60) return 'low';
    if (activeRate < 80) return 'medium';
    return 'high';
}

const getActiveRateColorKey = (activeRate: number): TailwindColorKey =>
    RATE_BAND_COLORS[activeRateBand(activeRate)];

export const getActiveRateColorClass = (activeRate: number): string =>
    getTextColorClass(getActiveRateColorKey(activeRate));

export const getActiveRateChartFill = (activeRate: number): string =>
    getChartFill(getActiveRateColorKey(activeRate));

/** Deployment Rate Band Colors */
function deploymentRateBand(deployRate: number): RateBand {
    if (deployRate < 60) return 'low';
    if (deployRate < 80) return 'medium';
    return 'high';
}

const getDeploymentRateColorKey = (deployRate: number): TailwindColorKey =>
    RATE_BAND_COLORS[deploymentRateBand(deployRate)];

export const getDeploymentRateColorClass = (deployRate: number): string =>
    getTextColorClass(getDeploymentRateColorKey(deployRate));

/** Utilization Band Colors */
type UtilizationBand = 'ok' | 'warning' | 'critical';

const UTILIZATION_BAND_COLORS: Record<UtilizationBand, TailwindColorKey> = {
    ok: 'green',
    warning: 'yellow',
    critical: 'red',
};

function utilizationBand(util: number): UtilizationBand {
    if (util >= 100) return 'critical';
    if (util >= 90) return 'warning';
    return 'ok';
}

const getUtilizationColorKey = (util: number): TailwindColorKey =>
    UTILIZATION_BAND_COLORS[utilizationBand(util)];

export const getUtilizationTextColorClass = (util: number): string =>
    getTextColorClass(getUtilizationColorKey(util));

export const getUtilizationBGColorClass = (util: number): string =>
    getBgColorClass(getUtilizationColorKey(util));

export const getUtilizationChartFill = (util: number): string =>
    getChartFill(getUtilizationColorKey(util));

/** Consumption Type */
export enum DataConsumptionType {
    TOTAL,
    CONTRACTED,
    CONSUMED,
    OVERAGE,
    UNUSED,
    CAPPED_FORECAST,
    UNCAPPED_FORECAST,
    PREVIOUS_MONTH,
    PRIORITY_DATA,
    STANDARD_DATA,
}

const DATA_CONSUMPTION_TYPE_COLORS: Record<DataConsumptionType, TailwindColorKey> = {
    [DataConsumptionType.TOTAL]: 'blue',
    [DataConsumptionType.CONTRACTED]: 'black',
    [DataConsumptionType.CONSUMED]: 'green',
    [DataConsumptionType.OVERAGE]: 'red',
    [DataConsumptionType.UNUSED]: 'gray',
    [DataConsumptionType.CAPPED_FORECAST]: 'purple',
    [DataConsumptionType.UNCAPPED_FORECAST]: 'indigo',
    [DataConsumptionType.PREVIOUS_MONTH]: 'orange',
    [DataConsumptionType.PRIORITY_DATA]: 'orbiusBlue',
    [DataConsumptionType.STANDARD_DATA]: 'standardData',
};

const getDataConsumptionTypeColorKey = (type: DataConsumptionType): TailwindColorKey =>
    colorKeyFromMap(DATA_CONSUMPTION_TYPE_COLORS, type);

export const getDataConsumptionTypeBGColorClass = (type: DataConsumptionType): string =>
    getBadgeColorClasses(getDataConsumptionTypeColorKey(type));

export const getDataConsumptionTypeTextColorClass = (type: DataConsumptionType): string =>
    getTextColorClass(getDataConsumptionTypeColorKey(type));

export const getDataConsumptionTypeChartFill = (type: DataConsumptionType): string =>
    getChartFill(getDataConsumptionTypeColorKey(type));
